import { useCallback } from 'preact/hooks';

import { usePress } from '../hooks/UNSAFE_usePress';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useNavigationListContext } from './useNavigationListContext';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from '../UNSAFE_NavigationList/themes/NavigationListItemStyles.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/NavigationListItemTheme';
import { useUser } from '../hooks/UNSAFE_useUser';

const flexItemStyleInterpolations = mergeInterpolations<FlexitemProps>([
  ...Object.values(flexitemInterpolations)
]);
const { class: labelContainerFlexItemClasses, ...labelContainerFlexItemStyle } =
  flexItemStyleInterpolations({
    alignSelf: 'center'
  });

export function useNavigationListItem<K extends string | number>({ itemKey }: { itemKey: K }) {
  const {
    onSelectionChange,
    selection,
    currentKey,
    showFocusRing,
    navigationListItemPrefix,
    edge,
    display
  } = useNavigationListContext();

  //Selection and focus state
  const isCurrent = currentKey === itemKey;

  const isSelected = selection === itemKey;

  const isIconOnly = display === 'icons' ? true : false;
  const isEdgeEnd = edge === 'end' ? true : false;

  //Styling
  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const {
    classes: listItemClasses,
    styles: {
      labelContainerClasses,
      navigationItemStackedBadge,
      navigationItemStackedBadgeRtl,
      startIconStacked
    }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme,
    {
      focusRing: showFocusRing ? 'isFocusRing' : 'notFocusRing',
      current: isCurrent ? 'isCurrent' : 'notCurrtent',
      selected: isSelected ? 'isSelected' : 'notSelected',
      direction: isRtl ? 'isRtl' : 'notRtl',
      hybridHover: applyHoverStyle ? 'isHybridHover' : 'notHybridHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      active: applyActiveStyle ? 'isActive' : 'notActive',
      edgeEnd: edge === 'end' ? 'isEnd' : 'notEnd',
      iconOnly: isIconOnly ? 'isIconOnly' : 'notIconOnly'
    }
  );

  const navigationItemIconOnlyClasses = classNames([startIconStacked]);

  const navigationItemStackedBadgeClasses = classNames([
    navigationItemStackedBadge,
    direction === 'rtl' && navigationItemStackedBadgeRtl
  ]);

  //Item handlers
  const handlePress = useCallback(() => {
    onSelectionChange?.({ value: itemKey as K, reason: 'pointer' });
  }, [itemKey, onSelectionChange]);

  //Legacy navlist makes selection on keyDown, but since preact tabbar made selection after keyUp, will do the same
  //for preact navlist
  const { pressProps } = usePress(handlePress);

  return {
    itemId: navigationListItemPrefix + itemKey,
    itemHandlers: mergeProps(interactionProps, pressProps /*,onFocusProps*/),
    itemClasses: classNames([listItemClasses]),
    isSelected,
    labelContainerClasses: classNames([labelContainerClasses, labelContainerFlexItemClasses]),
    labelContainerStyle: labelContainerFlexItemStyle,
    isIconOnly,
    isEdgeEnd,
    stackedBadgeClasses: classNames([navigationItemStackedBadgeClasses]),
    iconOnlyClasses: classNames([navigationItemIconOnlyClasses]),
    isCurrent,
    showFocusRing
  };
}
